/**
 * Registry of every cached `useApi` read whose entry is invalidated from somewhere else.
 *
 * It lives in shared rather than next to the request that fills the entry: a cache key is a
 * contract between that request and every write that stales it, and those writes live in other
 * slices. Owning the key inside one feature would make every writer import that feature.
 */

type CacheMethod = "GET" | "POST";

/**
 * How wide the prefix reaches:
 * - `own` — only the entries of this exact url; nested paths survive;
 * - `subtree` — this url and everything below it.
 */
type CacheScope = "own" | "subtree";

interface CachedEndpoint {
  method: CacheMethod
  url: string
  scope: CacheScope
  /** Who clears it. Documentation, not a type — shared cannot reference features. */
  invalidatedBy: readonly string[]
}

export const CACHED_ENDPOINTS = {
  usageSummary: {
    method: "GET",
    url: "/llm-usage/summary",
    scope: "own",
    invalidatedBy: ["POST /email-management/get-preview"],
  },
  generations: {
    method: "GET",
    url: "/llm-usage/generations",
    // `own`: a finished run's detail (`/generations/{id}/prompt`) never changes, so its entry
    // must survive new rows being added to the list.
    scope: "own",
    invalidatedBy: ["POST /email-management/get-preview"],
  },
  brandsByUser: {
    method: "GET",
    url: "/email-management/get-brands-by-user",
    scope: "own",
    invalidatedBy: ["PUT /auth/user-accesses", "PUT /auth/brand-access"],
  },
  llmFeatures: {
    method: "GET",
    url: "/llm-usage/features",
    scope: "own",
    invalidatedBy: ["PUT /llm-settings"],
  },
  // `subtree`, unlike `llmFeatures` above: the roster lives at
  // `/llm-usage/features/{feature}/brands`, so the prefix has to reach below the list url. It
  // therefore also drops the list entry — which the same write invalidates anyway.
  llmFeatureBrands: {
    method: "GET",
    url: "/llm-usage/features",
    scope: "subtree",
    invalidatedBy: ["PUT /llm-settings"],
  },
  llmSettings: {
    method: "GET",
    url: "/llm-usage/settings",
    scope: "own",
    // The entry is keyed per `?feature=`, but the prefix stops at the url — so this drops every
    // feature's entry, not just the written one. Deliberate: the params serialisation is the
    // library's private business, and over-invalidating a settings GET costs one refetch.
    invalidatedBy: ["PUT /llm-settings"],
  },
  // The two MCP reads (`/mcp-usage/summary`, `/mcp-usage/calls`) are deliberately absent: they do
  // not opt into caching. Their figures move continuously — calls land, and Snowflake resolves
  // credits for calls that were already pending — so every read of them must reach the network.
} as const satisfies Record<string, CachedEndpoint>;

type CacheEntry = keyof typeof CACHED_ENDPOINTS;

/**
 * The only place the library's key format is written down. `cache: true` keys an entry as
 * `auto:METHOD:url:params:data`, so the trailing `:` — the url/params separator — is exactly
 * what cuts nested paths off a prefix.
 */
const toPrefix = ({ method, url, scope }: CachedEndpoint): string =>
  `auto:${method}:${url}${scope === "own" ? ":" : ""}`;

export const CACHE_PREFIXES = Object.fromEntries(
  Object.entries(CACHED_ENDPOINTS).map(([key, endpoint]) => [key, toPrefix(endpoint)]),
) as Record<CacheEntry, string>;
